/**
 * Shared taxonomy for Discover preferences — the canonical source of
 * user-facing labels for each topic/vertical value stored in
 * discover_preferences.topics and .verticals. Used by FirstVisitModal (the
 * picker) and DiscoverContextPanel (the sidebar chip display) so both surfaces
 * render the same friendly text instead of drifting.
 */
import {
  BarbellIcon,
  BriefcaseIcon,
  CpuIcon,
  DevicesIcon,
  DiamondIcon,
  ForkKnifeIcon,
  GlobeIcon,
  HouseIcon,
  LeafIcon,
  NewspaperIcon,
  PaintBrushIcon,
  PawPrintIcon,
  SparkleIcon,
  StarIcon,
  TShirtIcon,
} from '@phosphor-icons/react';
import type { ComponentType } from 'react';

export interface TaxonomyEntry {
  label: string;
  value: string;
  icon: ComponentType<{ className?: string; weight?: 'regular' | 'fill' | 'duotone' }>;
}

// Canonical topic axis — shared between FirstVisitModal (onboarding picker),
// DiscoverContextPanel (sidebar chips), DiscoverTabs (desktop dropdown), and
// DiscoverMobileTabs (horizontal row). Nine values, each guaranteed to have
// Mediastack + RSS content:
//   - World / Business / Tech / AI / Entertainment: backed by Mediastack's
//     native categories (general / business / technology / entertainment) plus
//     AI-keyword gating on top of technology.
//   - Marketing / Creator / Social / Design: Mora-niche RSS (Buffer, HubSpot,
//     Digiday, creator blogs, Apartment Therapy, Design Milk).
export const NEWS_TOPICS: TaxonomyEntry[] = [
  { label: 'World', value: 'general', icon: GlobeIcon },
  { label: 'Business', value: 'commerce', icon: BriefcaseIcon },
  { label: 'Tech', value: 'tech', icon: CpuIcon },
  { label: 'AI', value: 'ai', icon: SparkleIcon },
  { label: 'Entertainment', value: 'lifestyle', icon: StarIcon },
  { label: 'Marketing', value: 'marketing', icon: NewspaperIcon },
  { label: 'Creator', value: 'creator', icon: StarIcon },
  { label: 'Social', value: 'social', icon: DevicesIcon },
  { label: 'Design', value: 'design', icon: PaintBrushIcon },
];

export const VERTICALS: TaxonomyEntry[] = [
  { label: 'Fashion & Apparel', value: 'fashion', icon: TShirtIcon },
  { label: 'Beauty & Skincare', value: 'beauty', icon: SparkleIcon },
  { label: 'Home & Living', value: 'home', icon: HouseIcon },
  { label: 'Food & Beverage', value: 'food', icon: ForkKnifeIcon },
  { label: 'Fitness & Wellness', value: 'fitness', icon: BarbellIcon },
  { label: 'Pets', value: 'pets', icon: PawPrintIcon },
  { label: 'Tech Gadgets', value: 'tech_gadgets', icon: DevicesIcon },
  { label: 'Jewelry', value: 'jewelry', icon: DiamondIcon },
  { label: 'Sustainable', value: 'sustainable', icon: LeafIcon },
];

const TOPIC_LOOKUP = new Map(NEWS_TOPICS.map((t) => [t.value, t]));
const VERTICAL_LOOKUP = new Map(VERTICALS.map((v) => [v.value, v]));

export function getTopicEntry(value: string): TaxonomyEntry | undefined {
  return TOPIC_LOOKUP.get(value);
}

export function getVerticalEntry(value: string): TaxonomyEntry | undefined {
  return VERTICAL_LOOKUP.get(value);
}

/**
 * Suggested brand chips per vertical — surfaced in FirstVisitModal when a
 * vertical is selected so the user has a one-tap way to populate followed
 * brands.
 */
export const SUGGESTED_BRANDS_BY_VERTICAL: Record<string, string[]> = {
  fashion: ['Nike', 'Lululemon', 'Aritzia', 'Zara', 'Uniqlo', 'Everlane'],
  beauty: ['Sephora', 'Glossier', 'Fenty Beauty', 'Rare Beauty', 'Drunk Elephant', 'Ilia'],
  home: ['West Elm', 'CB2', 'Parachute', 'Brooklinen', 'IKEA', 'Pottery Barn'],
  food: ['Trader Joe\u2019s', 'Whole Foods', 'Olipop', 'Poppi', 'Liquid Death'],
  fitness: ['Peloton', 'Alo Yoga', 'Gymshark', 'Nike', 'Bala', 'Tonal'],
  pets: ['Chewy', 'BarkBox', 'Wild One', 'Farmer\u2019s Dog'],
  tech_gadgets: ['Apple', 'Anker', 'DJI', 'Samsung', 'Sonos'],
  jewelry: ['Mejuri', 'Catbird', 'Missoma', 'Pandora'],
  sustainable: ['Patagonia', 'Allbirds', 'Reformation', 'Everlane', 'Parachute'],
  // service / consulting / creator / company / agency-specific suggestions
  coaching: ['Tony Robbins', 'Mel Robbins', 'Brene Brown', 'James Clear'],
  consulting: ['McKinsey', 'BCG', 'Bain', 'Deloitte', 'Accenture'],
  agency_services: ['Ogilvy', 'Wieden+Kennedy', 'BBDO', 'AKQA'],
  saas: ['Stripe', 'Notion', 'Linear', 'Vercel', 'Figma', 'Slack'],
  fintech: ['Stripe', 'Plaid', 'Mercury', 'Ramp', 'Brex'],
  healthtech: ['Oura', 'Whoop', 'Hims', 'Ro', 'One Medical'],
  ecommerce_platform: ['Shopify', 'WooCommerce', 'BigCommerce', 'Wix'],
  devtools: ['Vercel', 'GitHub', 'Linear', 'Sentry', 'Datadog'],
  creator_fitness: ['Whitney Simmons', 'Kayla Itsines', 'Chloe Ting', 'Natacha Oceane'],
  creator_fashion: ['Matilda Djerf', 'Brittany Xavier', 'Aimee Song'],
  creator_food: ['Joshua Weissman', 'Smitten Kitchen', 'Tasty', 'Bon Appetit'],
  creator_tech: ['MKBHD', 'Linus Tech Tips', 'iJustine'],
  creator_parenting: ['Taking Cara Babies', 'Big Little Feelings'],
  creator_travel: ['The Blonde Abroad', 'Eva zu Beck'],
  creator_gaming: ['Ninja', 'Pokimane', 'Valkyrae'],
};

// ============================================================================
// ICP-aware taxonomy — 2026-04-20
// Branches labels + chip sets by profiles.business_type. Everyone saves to the
// same discover_preferences.topics / .verticals arrays; only the rendered
// labels differ so each ICP gets a coherent picker.
// ============================================================================

export type BusinessType =
  'ecommerce' | 'personal_brand' | 'service' | 'creator' | 'company' | 'agency';

export interface IcpTaxonomy {
  topicsTitle: string;
  topics: TaxonomyEntry[];
  verticalsTitle: string;
  verticals: TaxonomyEntry[];
  modalSubtitle: string;
  /**
   * Copy for the single tracked-brands panel (backed by the `competitors`
   * table's relationship_kind, see businessTypeToRelationshipKind). The
   * same real entities read as "Competitors" for a service business,
   * "Your Competitors"/"Competitors" for creator/company, "Followed Brands"
   * (aspirational, not necessarily rivals) for ecommerce, and "Client
   * Accounts" (not competitive intel) for an agency.
   */
  brandsTitle: string;
  brandsDescription: string;
}

// Reusable topic entry builders — keeps ICP variants typed against the same
// taxonomy shape without duplicating icons.
const T = (label: string, value: string, icon: TaxonomyEntry['icon']): TaxonomyEntry => ({
  label,
  value,
  icon,
});

const PERSONAL_BRAND_TOPICS: TaxonomyEntry[] = [
  T('Leadership', 'leadership', StarIcon),
  T('Career', 'career', BriefcaseIcon),
  T('Productivity', 'productivity', SparkleIcon),
  T('Tech & AI', 'ai', SparkleIcon),
  T('Business', 'commerce', BriefcaseIcon),
  T('Health', 'health', LeafIcon),
  T('Marketing', 'marketing', NewspaperIcon),
  T('Culture', 'lifestyle', StarIcon),
];

const PERSONAL_BRAND_VERTICALS: TaxonomyEntry[] = [
  T('Coaching', 'coaching', SparkleIcon),
  T('Speaking', 'speaking', StarIcon),
  T('Education', 'education', PaintBrushIcon),
  T('Consulting', 'consulting', BriefcaseIcon),
  T('Writing / Author', 'writing', NewspaperIcon),
  T('Executive / Thought-leadership', 'thought_leadership', StarIcon),
];

const SERVICE_TOPICS: TaxonomyEntry[] = [
  T('Business', 'commerce', BriefcaseIcon),
  T('Finance', 'finance', DiamondIcon),
  T('Leadership', 'leadership', StarIcon),
  T('Marketing', 'marketing', NewspaperIcon),
  T('Productivity', 'productivity', SparkleIcon),
  T('AI', 'ai', SparkleIcon),
  T('Health', 'health', LeafIcon),
  T('Career', 'career', BriefcaseIcon),
];

const SERVICE_VERTICALS: TaxonomyEntry[] = [
  T('Coaching', 'coaching', SparkleIcon),
  T('Consulting', 'consulting', BriefcaseIcon),
  T('Agency Services', 'agency_services', StarIcon),
  T('Legal', 'legal', BriefcaseIcon),
  T('Accounting', 'accounting', BriefcaseIcon),
  T('Real Estate', 'real_estate', HouseIcon),
  T('Healthcare', 'healthcare', LeafIcon),
  T('Education', 'education', PaintBrushIcon),
];

const CREATOR_TOPICS: TaxonomyEntry[] = [
  T('Tech', 'tech', CpuIcon),
  T('Entertainment', 'lifestyle', StarIcon),
  T('Lifestyle', 'creator', SparkleIcon),
  T('AI', 'ai', SparkleIcon),
  T('Fashion', 'fashion_news', TShirtIcon),
  T('Food', 'food_news', ForkKnifeIcon),
  T('Sports', 'sports', BarbellIcon),
  T('Gaming', 'gaming', DevicesIcon),
  T('Design', 'design', PaintBrushIcon),
];

const CREATOR_VERTICALS: TaxonomyEntry[] = [
  T('Fitness', 'creator_fitness', BarbellIcon),
  T('Fashion', 'creator_fashion', TShirtIcon),
  T('Food', 'creator_food', ForkKnifeIcon),
  T('Tech', 'creator_tech', CpuIcon),
  T('Beauty', 'beauty', SparkleIcon),
  T('Parenting', 'creator_parenting', HouseIcon),
  T('Travel', 'creator_travel', GlobeIcon),
  T('Finance', 'finance', DiamondIcon),
  T('Gaming', 'creator_gaming', DevicesIcon),
];

const COMPANY_TOPICS: TaxonomyEntry[] = [
  T('Tech', 'tech', CpuIcon),
  T('Business', 'commerce', BriefcaseIcon),
  T('AI', 'ai', SparkleIcon),
  T('SaaS', 'saas_news', DevicesIcon),
  T('Startup', 'startup', SparkleIcon),
  T('Product', 'product', PaintBrushIcon),
  T('Engineering', 'engineering', CpuIcon),
  T('Marketing', 'marketing', NewspaperIcon),
];

const COMPANY_VERTICALS: TaxonomyEntry[] = [
  T('SaaS', 'saas', DevicesIcon),
  T('Fintech', 'fintech', DiamondIcon),
  T('Healthtech', 'healthtech', LeafIcon),
  T('E-commerce platform', 'ecommerce_platform', BriefcaseIcon),
  T('Devtools', 'devtools', CpuIcon),
  T('Security', 'security', SparkleIcon),
  T('AI/ML', 'ai_ml', SparkleIcon),
  T('Hardware', 'hardware', DevicesIcon),
];

const AGENCY_TOPICS: TaxonomyEntry[] = [
  T('Business', 'commerce', BriefcaseIcon),
  T('Marketing', 'marketing', NewspaperIcon),
  T('Design', 'design', PaintBrushIcon),
  T('Tech', 'tech', CpuIcon),
  T('Social', 'social', DevicesIcon),
  T('Creator', 'creator', StarIcon),
  T('AI', 'ai', SparkleIcon),
  T('Lifestyle', 'lifestyle', StarIcon),
];

const AGENCY_VERTICALS: TaxonomyEntry[] = [
  T('Fashion', 'fashion', TShirtIcon),
  T('Beauty', 'beauty', SparkleIcon),
  T('Food', 'food', ForkKnifeIcon),
  T('Tech', 'tech_gadgets', DevicesIcon),
  T('Hospitality', 'hospitality', HouseIcon),
  T('Real Estate', 'real_estate', HouseIcon),
  T('Finance', 'finance', DiamondIcon),
  T('Healthcare', 'healthcare', LeafIcon),
  T('B2B SaaS', 'saas', DevicesIcon),
];

export const TAXONOMY_BY_BUSINESS_TYPE: Record<BusinessType, IcpTaxonomy> = {
  ecommerce: {
    topicsTitle: 'News & topics',
    topics: NEWS_TOPICS,
    verticalsTitle: 'What you\u2019re interested in',
    verticals: VERTICALS,
    modalSubtitle:
      'Pick the beats you cover, the categories you care about, and the brands you follow. We\u2019ll surface what\u2019s breaking first and skip everything else.',
    brandsTitle: 'Followed Brands',
    brandsDescription: 'Follow brands you admire to see stories that mention them first.',
  },
  personal_brand: {
    topicsTitle: 'News & topics',
    topics: PERSONAL_BRAND_TOPICS,
    verticalsTitle: 'Your expertise',
    verticals: PERSONAL_BRAND_VERTICALS,
    modalSubtitle:
      'Pick the beats you write about and the expertise areas you build authority in. We\u2019ll surface thought-leadership hooks and skip the noise.',
    brandsTitle: 'Thought Leaders',
    brandsDescription: 'Follow the voices you study, so you can respond to their takes.',
  },
  service: {
    topicsTitle: 'Industry news',
    topics: SERVICE_TOPICS,
    verticalsTitle: 'Your expertise',
    verticals: SERVICE_VERTICALS,
    modalSubtitle:
      'Pick the industry beats you follow and the expertise areas you work in. We\u2019ll surface thought-leadership angles and skip the noise.',
    /* Competitors, not "Thought Leaders": the rows are `competitors` with
       relationship_kind 'competitor' (useCompetitorRelationshipKind), and what a
       firm types here is its rivals — ITC Tax entered DMA, Ryan and KE Andrews.
       The old title promised voices and stored rivals. */
    brandsTitle: 'Competitors',
    brandsDescription: 'The firms you compete with for the same clients.',
  },
  creator: {
    topicsTitle: 'News you cover',
    topics: CREATOR_TOPICS,
    verticalsTitle: 'Your pillars',
    verticals: CREATOR_VERTICALS,
    modalSubtitle:
      'Pick the news beats you post about and the pillars your audience shows up for. We\u2019ll surface what\u2019s trending in your niche.',
    brandsTitle: 'Your Competitors',
    brandsDescription:
      'Follow the creators you study \u2014 we\u2019ll surface their mentions and trends first.',
  },
  company: {
    topicsTitle: 'Industry news',
    topics: COMPANY_TOPICS,
    verticalsTitle: 'Your sector',
    verticals: COMPANY_VERTICALS,
    modalSubtitle:
      'Pick the tech beats you track and the sector you operate in. We\u2019ll surface category signal and skip irrelevant noise.',
    brandsTitle: 'Competitors',
    brandsDescription: 'Follow category competitors \u2014 launches, funding, narrative beats.',
  },
  agency: {
    topicsTitle: 'Client industries',
    topics: AGENCY_TOPICS,
    verticalsTitle: 'Client verticals',
    verticals: AGENCY_VERTICALS,
    modalSubtitle:
      'Pick the beats your clients care about and the verticals you serve. We\u2019ll surface what\u2019s relevant across your client roster.',
    brandsTitle: 'Client Accounts',
    brandsDescription: 'Follow your client brands to track the whole roster.',
  },
};

export function getTaxonomyFor(businessType: string | null | undefined): IcpTaxonomy {
  if (businessType && businessType in TAXONOMY_BY_BUSINESS_TYPE) {
    return TAXONOMY_BY_BUSINESS_TYPE[businessType as BusinessType];
  }
  return TAXONOMY_BY_BUSINESS_TYPE.ecommerce;
}

export const getTaxonomy = getTaxonomyFor;

// ============================================================================
// ICP-aware audience suggestions — used by Plan Detail Target Audience picker.
// Plain strings (no icons) because audiences read as editorial phrases.
// ============================================================================

const AUDIENCE_SUGGESTIONS_BY_ICP: Record<BusinessType, string[]> = {
  ecommerce: [
    'First-time buyers',
    'Gift-shoppers',
    'Loyal repeat customers',
    'Deal-hunters',
    'Subscription browsers',
    'Wedding / event gifters',
  ],
  personal_brand: [
    'Early-career professionals',
    'Mid-career pivoters',
    'Peer thought-leaders',
    'Aspiring founders',
    'Speakers and authors',
    'Coaching clients',
  ],
  service: [
    'Small-business owners',
    'Solo practitioners',
    'Enterprise decision-makers',
    'Referral partners',
    'First-time clients',
    'Long-term retainer clients',
  ],
  creator: [
    'Core subscribers',
    'Casual scrollers',
    'Brand partners',
    'Niche enthusiasts',
    'First-time viewers',
    'Community superfans',
  ],
  company: [
    'Technical decision-makers',
    'Business buyers',
    'Developer evangelists',
    'C-suite executives',
    'Procurement teams',
    'Existing customers',
  ],
  agency: [
    'Marketing directors',
    'Brand managers',
    'In-house creatives',
    'Founder-CMOs',
    'Performance marketers',
    'New business prospects',
  ],
};

export function getSuggestedAudiencesFor(businessType: string | null | undefined): string[] {
  if (businessType && businessType in AUDIENCE_SUGGESTIONS_BY_ICP) {
    return AUDIENCE_SUGGESTIONS_BY_ICP[businessType as BusinessType];
  }
  return AUDIENCE_SUGGESTIONS_BY_ICP.ecommerce;
}

// Unified lookup for rendering chips in side panels — falls back across every
// ICP's taxonomy so saved values render with the right label regardless of
// which ICP picked them.
export function resolveEntry(value: string, axis: 'topic' | 'vertical'): TaxonomyEntry | undefined {
  for (const tx of Object.values(TAXONOMY_BY_BUSINESS_TYPE)) {
    const pool = axis === 'topic' ? tx.topics : tx.verticals;
    const match = pool.find((e) => e.value === value);
    if (match) return match;
  }
  return axis === 'topic' ? getTopicEntry(value) : getVerticalEntry(value);
}
